import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Upload, Download, Trash2, File, Presentation, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function WorkshopDocuments({ eventId, isOwner }) {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [uploading, setUploading] = useState(false);
  
  const [newDoc, setNewDoc] = useState({
    title: "",
    category: "agenda",
    description: ""
  });

  const categoryOptions = [
    { value: "agenda", label: "📋 Agenda", icon: FileText },
    { value: "presentation", label: "📊 Präsentation", icon: Presentation },
    { value: "handout", label: "📄 Handout", icon: File },
    { value: "notes", label: "📝 Notizen", icon: FileText },
    { value: "other", label: "📁 Sonstiges", icon: File }
  ];

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, [eventId]);

  const fetchDocuments = async (cat = null) => {
    try {
      const url = cat && cat !== "all"
        ? `${API}/events/${eventId}/workshop-docs?category=${cat}`
        : `${API}/events/${eventId}/workshop-docs`;
      const response = await axios.get(url);
      setDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/workshop-docs/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    
    if (!newDoc.title.trim()) {
      toast.error("Bitte Titel eingeben!");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      toast.loading("Dokument wird hochgeladen...", { id: "doc-upload" });
      
      const queryParams = new URLSearchParams({
        title: newDoc.title,
        category: newDoc.category,
        description: newDoc.description
      });
      
      await axios.post(
        `${API}/events/${eventId}/workshop-docs?${queryParams}`,
        formData,
        { 
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      
      toast.success("Dokument hochgeladen! 📄", { id: "doc-upload" });
      setNewDoc({ title: "", category: "agenda", description: "" });
      fetchDocuments();
      fetchCategories();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen", { id: "doc-upload" });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Track download
      await axios.patch(`${API}/events/${eventId}/workshop-docs/${doc.id}/download`);
      
      // Open document in new tab
      window.open(`${BACKEND_URL}${doc.file_url}`, '_blank');
      
      // Refresh to show updated download count
      fetchDocuments();
    } catch (error) {
      console.error("Download tracking failed:", error);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Dokument wirklich löschen?")) return;

    try {
      await axios.delete(
        `${API}/events/${eventId}/workshop-docs/${docId}`,
        { withCredentials: true }
      );
      toast.success("Dokument gelöscht");
      fetchDocuments();
      fetchCategories();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleCategoryFilter = (cat) => {
    setSelectedCategory(cat);
    fetchDocuments(cat);
  };

  const getFileIcon = (fileType) => {
    if (fileType === '.pdf') return '📕';
    if (['.docx', '.doc'].includes(fileType)) return '📘';
    if (['.pptx', '.ppt'].includes(fileType)) return '📊';
    if (['.xlsx', '.xls'].includes(fileType)) return '📗';
    return '📄';
  };

  const getCategoryIcon = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat ? cat.label.split(' ')[0] : '📁';
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <FileText className="w-5 h-5 text-primary" />
          Workshop-Dokumente
        </h3>
        <p className="text-sm text-muted-foreground">
          Alle wichtigen Dokumente an einem Ort 📚
        </p>
      </div>

      {isOwner ? (
        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="documents">Dokumente</TabsTrigger>
            <TabsTrigger value="upload">Hochladen</TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleCategoryFilter("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === "all"
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                Alle
              </button>
              {categoryOptions.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryFilter(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Document List */}
            <div className="space-y-3">
              {documents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Noch keine Dokumente</p>
                </div>
              ) : (
                documents.map((doc) => (
                  <div key={doc.id} className="p-4 border rounded-lg bg-card hover:bg-secondary/20 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="text-4xl">{getFileIcon(doc.file_type)}</div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h5 className="font-semibold flex items-center gap-2">
                              {doc.title}
                              <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                                {getCategoryIcon(doc.category)} {categoryOptions.find(c => c.value === doc.category)?.label.split(' ')[1]}
                              </span>
                            </h5>
                            <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mb-2">{doc.description}</p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex gap-3 text-xs text-muted-foreground">
                            <span>📦 {doc.file_size_mb} MB</span>
                            <span>⬇️ {doc.downloads} Downloads</span>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDownload(doc)}
                          >
                            <Download className="w-3 h-3 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-4">
            <div className="p-4 bg-secondary/20 rounded-lg space-y-3">
              <h4 className="font-medium">📤 Dokument hochladen</h4>
              <p className="text-sm text-muted-foreground">
                Unterstützte Formate: PDF, DOCX, PPTX, XLSX
              </p>
              
              <div>
                <Label>Titel *</Label>
                <Input
                  value={newDoc.title}
                  onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                  placeholder="z.B. Workshop-Agenda"
                />
              </div>

              <div>
                <Label>Kategorie</Label>
                <Select
                  value={newDoc.category}
                  onValueChange={(val) => setNewDoc({ ...newDoc, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  value={newDoc.description}
                  onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
                  placeholder="Kurze Beschreibung des Dokuments..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Datei *</Label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                  className="hidden"
                  id="doc-upload"
                  onChange={(e) => handleUpload(e.target.files[0])}
                  disabled={uploading}
                />
                <label htmlFor="doc-upload">
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={uploading || !newDoc.title.trim()}
                    asChild
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? "Lädt hoch..." : "Datei auswählen & hochladen"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-6">
          {/* Category Filter for Guests */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleCategoryFilter("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === "all"
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              Alle
            </button>
            {categoryOptions.map(cat => (
              <button
                key={cat.value}
                onClick={() => handleCategoryFilter(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Document Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {documents.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-muted-foreground">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Keine Dokumente in dieser Kategorie</p>
              </div>
            ) : (
              documents.map((doc) => (
                <div key={doc.id} className="p-4 border rounded-lg bg-card hover:bg-secondary/20 transition-colors cursor-pointer" onClick={() => handleDownload(doc)}>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{getFileIcon(doc.file_type)}</div>
                    <div className="flex-1">
                      <h5 className="font-semibold mb-1 flex items-center gap-2">
                        {doc.title}
                      </h5>
                      <p className="text-xs text-muted-foreground mb-1">{doc.file_name}</p>
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full inline-block mb-2">
                        {getCategoryIcon(doc.category)} {categoryOptions.find(c => c.value === doc.category)?.label.split(' ')[1]}
                      </span>
                      
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mb-2">{doc.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>📦 {doc.file_size_mb} MB</span>
                          <span>⬇️ {doc.downloads}</span>
                        </div>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}>
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
